import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import {
  createOneFormTemplateMutation,
  findByIdFormTemplateQuery,
  removeByIdFormTemplateMutation,
} from '~/shared/graphql-queries';
import findManyFormTemplateQuery from '~/shared/graphql-queries/form-templates/find-many-form-template-query';
import findOneFormTemplateQuery from '~/shared/graphql-queries/form-templates/find-one-form-template-query';
import formTemplateFragment from '~/shared/graphql-queries/form-templates/form-template-fragment';
import updateByIdFormTemplateMutation from '~/shared/graphql-queries/form-templates/update-by-id-form-template-mutation';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationFormTemplateResponse = Readonly<{
  data?: {
    paginationFormTemplateQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationFormTemplateResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type PaginationFormTemplateArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

type FindByIdFormTemplateArgs = Readonly<{
  _id?: string;
}>;

type FindOneFormTemplateArgs = Readonly<{
  filter?: Record<string, unknown>;
}>;

type RemoveByIdFormTemplateArgs = Readonly<{
  _id?: string;
}>;

type CreateOneFormTemplateArgs = Readonly<{
  record?: Record<string, unknown>;
}>;

type FindManyFormTemplateArgs = Readonly<{
  limit?: number;
  filter?: Record<string, unknown>;
}>;

type UpdateByIdFormTemplateArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
}>;

const transformFormTemplateResponse =
  (type: 'paginationFormTemplateQuery') =>
  (response: PaginationFormTemplateResponse): PaginationFormTemplateResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const formTemplatesTagTypes = ['form-templates'] as const;

export const getFormTemplatesQuery = (builder: BuilderType) => {
  return {
    paginationFormTemplate: builder.query<PaginationFormTemplateResult, PaginationFormTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'formTemplate',
              fragment: formTemplateFragment,
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_DESC,
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['form-templates'],
      transformResponse: transformFormTemplateResponse('paginationFormTemplateQuery'),
    }),

    findByIdFormTemplate: builder.query<unknown, FindByIdFormTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByIdFormTemplateQuery),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    findOneFormTemplate: builder.query<unknown, FindOneFormTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findOneFormTemplateQuery),
          variables: {
            filter: args?.filter,
          },
        }),
      }),
    }),

    removeByIdFormTemplate: builder.mutation<unknown, RemoveByIdFormTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByIdFormTemplateMutation),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    createOneFormTemplate: builder.mutation<unknown, CreateOneFormTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneFormTemplateMutation),
          variables: {
            record: args?.record,
          },
        }),
      }),
    }),

    findManyFormTemplate: builder.query<unknown, FindManyFormTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findManyFormTemplateQuery),
          variables: {
            limit: args?.limit,
            filter: args?.filter,
          },
        }),
      }),
    }),

    updateByIdFormTemplate: builder.mutation<unknown, UpdateByIdFormTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdFormTemplateMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
      }),
    }),
  };
};
